import Footer from "@/src/common/components/GlobalSearchModal/Footer"
import RecentResearch from "@/src/common/components/GlobalSearchModal/RecentSearch"
import RecentlyVisitedPage from "@/src/common/components/GlobalSearchModal/RecentlyVisitedPage"
import SearchResult, {
  SearchResultList,
  metaData
} from "@/src/common/components/GlobalSearchModal/SearchResult"
import SuggestedAction from "@/src/common/components/GlobalSearchModal/SuggestedAction"
import {
  GlobalSearchState,
  SearchFilter,
  SearchResultsType
} from "@/src/common/types"
import { useGlobalSearch } from "@/src/contexts/GlobalSearchContext"
import { useAnalytics } from "@/src/sb/components/Analytics"
import SearchInputV2 from "@/src/sb/components/Input/SearchInputV2"
import SearchInputV3 from "@/src/sb/components/Input/SearchInputV3"
import Skeleton from "@/src/sb/components/Skeleton"
import { USE_SEMANTIC_SEARCH_QUERY_PARAM } from "@/src/sb/constants"
import PageLinks from "@/src/sb/constants/copy/pageLinks"
import {
  CLEAR_RECENTLY_VISITED_PAGE,
  ENABLE_AI_SEARCH,
  ENABLE_CLEAR_ALL_SEARCHES
} from "@/src/sb/constants/featureFlags"
import { generateListSummary } from "@/src/sb/core/utils/helpers"
import { SBAPIInstance } from "@/src/sb/data-layer"
import { useUser } from "@/src/sb/hooks/useUser"
import { useFeatureValue } from "@growthbook/growthbook-react"
import classNames from "classnames"
import { format } from "date-fns"
import { debounce } from "lodash"
import { useRouter } from "next/router"
import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useHotkeys } from "react-hotkeys-hook"
import useSWR from "swr"

const DEBOUNCE_DELAY = 500
const NO_SELECTION = -1

type GlobalSearchType = {
  extraClassNames?: string
  defaultHeight?: string
}

const GlobalSearchModal = ({
  extraClassNames,
  defaultHeight
}: GlobalSearchType) => {
  const {
    isSearchOpen,
    closeSearch,
    onOpenFilters,
    setSearchTerm,
    sharedQuery,
    setSharedQuery,
    addRecentSearch,
    recentSearches,
    removeRecentSearch,
    addRecentlyVisitedPage,
    recentlyVisitedPages
  } = useGlobalSearch()
  const [searchState, setSearchState] = useState<GlobalSearchState>({})
  const [selectedIndex, setSelectedIndex] = useState(NO_SELECTION)

  const user = useUser()

  const clear_recently_visited_pages = useFeatureValue(
    CLEAR_RECENTLY_VISITED_PAGE,
    false
  )

  const enable_clear_all_searches = useFeatureValue(
    ENABLE_CLEAR_ALL_SEARCHES,
    false
  )

  const enable_ai_search = useFeatureValue(ENABLE_AI_SEARCH, false)

  const analytics = useAnalytics()

  const inputRef = useRef<HTMLInputElement>(null)

  const router = useRouter()

  const isSearchPage = router.asPath.startsWith(PageLinks.search)

  const USER_ID = user?.data?.user_id?.toString() || ""

  const semanticSearchQueryParam = router.query[USE_SEMANTIC_SEARCH_QUERY_PARAM]

  useEffect(() => {
    setSearchState((prev) => ({ ...prev, mounted: true }))
  }, [])

  useEffect(() => {
    if (isSearchOpen && sharedQuery) {
      handleGlobalSearchState(sharedQuery, "searchQuery")
      setSharedQuery("")
    }
  }, [isSearchOpen, sharedQuery])

  useEffect(() => {
    const handleRouteChange = () => {
      closeSearch()
    }

    router.events.on("routeChangeStart", handleRouteChange)

    return () => {
      router.events.off("routeChangeStart", handleRouteChange)
    }
  }, [router.events])

  const debouncedSetQuery = useMemo(
    () =>
      debounce((value: string, id?: number | string) => {
        setSearchState((prev) => ({
          ...prev,
          debounceQuery: value,
          result: {
            ...prev.result,
            activeId: id ?? prev.result?.activeId
          }
        }))
      }, DEBOUNCE_DELAY),
    []
  )

  const { data: searchFilters, isLoading } = useSWR<SearchFilter[]>(
    "globalSearchFilters",
    () => SBAPIInstance.fetchGlobalSearchFilters()
  )

  const { data: searchResults, isLoading: isSearchResultsLoading } = useSWR(
    searchState.debounceQuery && searchState.result?.activeId !== undefined
      ? [
          "globalSearchResults",
          searchState.debounceQuery,
          searchState.result.activeId
        ]
      : null,
    ([_, query, id]) => {
      const controller = new AbortController()
      const signal = controller.signal

      /* Important: cancel previous request when a new one starts
       SWR calls the fetcher with key; we use that as cache key */

      const promise = SBAPIInstance.fetchGlobalSearchResults(
        {
          q: query,
          filter_id: id,
          "use-semantic-reports": semanticSearchQueryParam === "true"
        },
        signal
      )

      ;(promise as any).cancel = () => controller.abort()

      return promise
    },
    {
      onSuccess: () => {
        setSearchState((prev) => ({
          ...prev,
          loading: false
        }))

        addRecentSearch({ name: searchState?.searchQuery as string }, USER_ID)
        analytics.updateSearchResults(
          searchState.debounceQuery as string,
          searchFilters?.find(
            (filter) => filter.id === searchState.result?.activeId
          )?.name as any,
          {
            all: searchResults.total
          }
        )
      }
    }
  )

  const resetSelectedIndex = () => {
    setSelectedIndex(NO_SELECTION)
  }

  const handleGlobalSearchState = (item: string, field: any) => {
    setSearchState((prev) => ({
      ...prev,
      [field]: item
    }))
    debouncedSetQuery(item, searchState.result?.activeId)
    resetSelectedIndex()
  }

  useEffect(() => {
    if (searchFilters?.length) {
      const firstFilter = searchFilters[0]
      setSearchState((prev) => ({
        ...prev,
        result: {
          ...prev.result,
          activeParent: firstFilter.name,
          activeSearch: firstFilter.name,
          activeId: firstFilter.id
        }
      }))
    }
  }, [searchFilters])

  useEffect(() => {
    setSearchState((prev) => ({
      ...prev,
      loading: !!searchState.searchQuery
    }))

    setSearchTerm(searchState.searchQuery as string)
  }, [searchState.searchQuery])

  useEffect(() => {
    if (isSearchOpen) {
      inputRef.current?.focus()
    }
  }, [isSearchOpen])

  const searchQueryResults = useMemo<SearchResultList[]>(
    () =>
      searchResults?.hits.map((data: SearchResultsType) => {
        const entityType = data.entityType

        let name: string | undefined
        let meta: metaData = {}

        switch (entityType) {
          case "transaction":
            name = (data as any).transactionTitle
            meta = {
              transactionHistory: "Transaction Profile",
              companyName: data?.companyName,
              announcementDate: data.announcementDate
                ? format(new Date(data.announcementDate), "MMM d, yyyy")
                : undefined
            }
            break

          case "report":
            name = data.title
            meta = {
              classification: data.classification,
              authorName: data.author,
              reportDate: data.date
                ? format(new Date(data.date), "MMM d, yyyy")
                : undefined
            }
            break

          case "company":
            name = data.name
            meta = {
              companyProfile: "Company Profile",
              subSectors: generateListSummary(data.subsectors),
              operatingCountries: generateListSummary(data.countriesOfOperation)
            }
            break

          case "dataset":
            name = data.name
            meta = {
              countryProfile: "Country Profile",
              classification: data.classification,
              operatingCountries: data.countryRiskName
            }
            break

          case "country_profile":
            name = data.name
            meta = {
              countryProfile: data.tags?.[0]?.name,
              countries: generateListSummary(data.countriesOfOperation),

              relatedIndustries: data?.metaTag?.name
            }
            break

          case "limited_partner":
            name = data.name
            meta = {
              limitedPartner: "Limited Partner",
              limitedPartnerType: generateListSummary(data?.investorTypes),
              geographicFocus:
                generateListSummary(data?.geoPreferences) ??
                generateListSummary(data?.countriesOfOperation)
            }
            break

          default:
            name = data.name ?? data.title
            meta = {
              profile: data.tags?.[0]?.name,
              industries: data?.metaTag?.name,
              countries: generateListSummary(data.countriesOfOperation)
            }
            break
        }

        return {
          id: data.objectId,
          name,
          metaData: [meta],
          type: entityType,
          slug: data.slug,
          groupSlug: data.groupSlug,
          bucketSlug: data.bucketSlug,
          companySlug: data.companySlug,
          countryRiskSlug: data.countryRiskSlug,
          tabSlug: data.tabSlug,
          txnSerialNo: data.serialNo
        }
      }) ?? [],
    [searchResults]
  )

  useHotkeys(
    "arrowdown",
    () => {
      setSelectedIndex((prev) => {
        const next = Math.min(prev + 1, searchQueryResults.length - 1)
        return next
      })
    },
    {
      enableOnFormTags: true
    },
    [searchQueryResults.length]
  )

  useHotkeys(
    "arrowup",
    () => {
      setSelectedIndex((prev) => {
        const next = Math.max(prev - 1, 0)
        return next
      })
    },
    {
      enableOnFormTags: true
    },
    [searchQueryResults.length]
  )

  const extractNames = (categories?: SearchFilter[]): string[] => {
    return categories?.flatMap((category) => category.name) ?? []
  }

  const listItems = useMemo(() => extractNames(searchFilters), [searchFilters])

  if (!searchState.mounted || !isSearchOpen) return null

  const handleTabChange = (
    searchItem: string,
    isChild = false,
    id?: number,
    activeParentItem?: string
  ) => {
    let obj = {}

    obj = {}
    if (isChild) {
      obj = activeParentItem
        ? {
            activeChild: searchItem,
            activeParent: activeParentItem
          }
        : { activeChild: searchItem }
    } else {
      obj = {
        activeParent: searchItem,
        activeChild: undefined
      }
    }

    setSearchState((prev) => ({
      ...prev,
      result: {
        ...prev.result,
        activeSearch: searchItem,
        ...obj,
        ...(id !== undefined && { activeId: id })
      }
    }))

    analytics.clickSearchResultTab(
      searchState.searchQuery as string,
      activeParentItem ?? (searchState?.result?.activeParent as string),
      searchItem,

      {
        all: searchResults?.hits?.length
      }
    )

    resetSelectedIndex()
  }

  const isSearchLoading = isSearchResultsLoading || searchState.loading

  const handleFilterToggle = () => {
    closeSearch()
    onOpenFilters()
  }

  const handleSearchSubmit = (query?: string) => {
    const q = (query || "").trim()
    if (!q) return
    const semanticParam = router.query[USE_SEMANTIC_SEARCH_QUERY_PARAM]
    let fullPath = `/search?q=${encodeURIComponent(q)}`
    if (semanticParam !== undefined) {
      fullPath += `&${USE_SEMANTIC_SEARCH_QUERY_PARAM}=${semanticParam}`
    }
    router.push(fullPath)
    addRecentlyVisitedPage(
      {
        name: q,
        slug: fullPath,
        type: "search"
      },
      USER_ID
    )
  }

  const handleNavigateToAi = (query?: string) => {
    if (query) setSharedQuery(query)
    closeSearch()
    router.push("/threads")
  }

  const ENABLED_ADDONS = !searchState?.searchQuery && !isSearchLoading

  return createPortal(
    <section
      className={classNames(
        "fixed inset-0 flex items-start justify-center p-2 overflow-y-auto z-50",
        extraClassNames
      )}
      onClick={() => closeSearch()}
    >
      <div
        className={classNames(
          "mt-[120px] w-full max-w-[750px] overflow-y-auto border-y-[0.5px] border-l-[0.5px] border-r-[0.5px] border-[#4B5563] bg-[#0E0F10] text-white font-smoothing-antialiased shadow-[_-3.04px_3.04px_24.28px_0px_#000] !no-scrollbar",
          defaultHeight
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex flex-col sticky top-0 z-10 bg-[#0E0F10]">
          <section className="border-b-[0.5px] border-[#4B5563] dark:border-[0.5px] bg-[#1A1D21]">
            {enable_ai_search ? (
              <SearchInputV3
                ref={inputRef}
                className={""}
                placeholder="Search for companies, transactions and more"
                value={searchState.searchQuery ?? ""}
                onChange={(event) =>
                  handleGlobalSearchState(event.target.value, "searchQuery")
                }
                isSearchPage={isSearchPage}
                onFilterToggle={handleFilterToggle}
                onSubmit={handleSearchSubmit}
                onNavigateToAi={handleNavigateToAi}
              />
            ) : (
              <SearchInputV2
                ref={inputRef}
                className={"bg-[#1F2937]"}
                placeholder="Search for companies, transactions and more"
                value={searchState.searchQuery ?? ""}
                onChange={(event) =>
                  handleGlobalSearchState(event.target.value, "searchQuery")
                }
                readOnly={false}
                cursorBehavior="default"
                isSearchPage={isSearchPage}
                onFilterToggle={handleFilterToggle}
                onClear={() => handleGlobalSearchState("", "searchQuery")}
              />
            )}
          </section>

          <nav
            className={classNames(
              "flex space-x-4 px-5 pt-2 pb-1 text-[#9CA3AF] overflow-auto border-b-[0.5px] border-[#4B5563]"
            )}
          >
            {isLoading ? (
              <ul className="flex gap-5 items-center text-sm leading-[22px] overflow-x-auto whitespace-nowrap border-b-[0.5px] border-[#4B5563]">
                {Array.from({ length: 10 }).map((_, index) => (
                  <li key={index} className="whitespace-nowrap">
                    <Skeleton height={15} width={50} />
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="flex gap-5 items-center text-sm leading-[22px] overflow-x-auto whitespace-nowrap focus:outline-none">
                {listItems?.map((item, index) => (
                  <li key={index}>
                    <button
                      onClick={() => {
                        const tab = searchFilters?.[index]
                        const firstChild =
                          tab && tab.children?.length > 0
                            ? tab?.children[0]
                            : null
                        if (firstChild) {
                          handleTabChange(
                            firstChild.name,
                            true,
                            firstChild.id,
                            item
                          )
                        } else {
                          handleTabChange(
                            item,
                            false,
                            searchFilters?.[index]?.id
                          )
                        }
                      }}
                      className={classNames(
                        "py-2 !text-xs tracking-[-0.01em]",
                        searchState.result?.activeSearch === item ||
                          searchState.result?.activeParent === item
                          ? "border-b border-white text-white focus:outline-none"
                          : "text-[#9CA3AF]"
                      )}
                    >
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </nav>

          {(
            searchFilters?.find(
              (item) => item?.name === searchState?.result?.activeParent
            )?.children ?? []
          ).length > 0 && (
            <div className="flex px-5 py-2.5 gap-2 overflow-auto text-[#9CA3AF]">
              {searchFilters
                ?.find(
                  (item) => item?.name === searchState?.result?.activeParent
                )
                ?.children?.map((child, index) => (
                  <button
                    key={index}
                    onClick={() => handleTabChange(child.name, true, child.id)}
                    className={`px-3 py-1 border whitespace-nowrap border-0.5 border-[#4B5563] focus:outline-none text-[10px]   ${
                      searchState?.result?.activeSearch === child.name
                        ? "text-white bg-[#1A1D21]"
                        : ""
                    }`}
                  >
                    {child.name}
                  </button>
                ))}
            </div>
          )}
        </header>

        <div className="flex-1 min-h-[40px] flex flex-col">
          <SearchResult
            searchList={searchQueryResults}
            isSearchResultsLoading={
              isSearchResultsLoading || searchState.loading
            }
            selectedResultsIndex={selectedIndex}
            onClickResult={(result, index) => {
              addRecentlyVisitedPage(result, USER_ID)
              analytics.clickRecentlyVisitedPage(
                result.name as string,
                result.type as string,
                index + 1,
                result.slug as string
              )
              analytics.clickSearchResult(
                searchState.searchQuery as string,
                searchState.result?.activeSearch as string,
                index + 1,
                result.name as string,
                result.type as string,
                result.slug as string
              )
            }}
          />
          {clear_recently_visited_pages && (
            <Fragment>
              {ENABLED_ADDONS && recentlyVisitedPages?.length > 0 && (
                <section className="md:block border-b-[0.5px] border-t-[0.5px] border-[#4B5563] py-1">
                  <RecentlyVisitedPage recentSearches={recentlyVisitedPages} />
                </section>
              )}
            </Fragment>
          )}
          {enable_clear_all_searches && (
            <Fragment>
              {ENABLED_ADDONS && recentSearches?.length > 0 && (
                <div className="md:block border-b-[0.5px] border-[#4B5563] py-1">
                  <RecentResearch
                    recentSearches={recentSearches}
                    onRemoveSearch={(data) => removeRecentSearch(data)}
                    onClickSearch={(data) => {
                      analytics.clickRecentSearchTerm(
                        data.name,
                        searchState?.result?.activeSearch as string
                      )
                      handleGlobalSearchState(data?.name, "searchQuery")
                    }}
                  />
                </div>
              )}
            </Fragment>
          )}

          {isSearchLoading || !searchState.searchQuery
            ? null
            : !searchQueryResults?.length && (
                <p className="py-3 px-5 text-xs text-[#9CA3AF] font-normal tracking-[-0.12px]">
                  No search result found
                </p>
              )}

          {ENABLED_ADDONS && <SuggestedAction />}
        </div>

        <section
          className={`sticky md:block bottom-0 z-10 flex-col bg-[#0E0F10] ${
            searchState?.searchQuery ? "" : "hidden"
          }`}
        >
          <Footer
            loading={isSearchLoading}
            searchResults={{
              hasSearchData: !!searchQueryResults?.length,
              searchQuery: searchState?.searchQuery,
              isSemanticSearch: semanticSearchQueryParam
                ? semanticSearchQueryParam === "true"
                : undefined
            }}
          />
        </section>
      </div>
    </section>,
    document.body
  )
}

export default GlobalSearchModal
